import './briefe.css';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fmtDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AREA_LABEL, duePill } from './labels';
import { LetterDialog } from './LetterDialog';
import type { BriefeTabResponse, KanzleiLetterItem, LetterArea } from './types';

// Reiter „Briefe“ eines Mandanten: alle Briefe meiner Bereiche, offene zuerst (knappste Frist oben), erledigte
// zum Aufklappen. Ein Brief öffnet sich über ?brief=<id>, damit „Ansehen“ aus der Leiste direkt dorthin führt.
export function BriefeTab({ clientId, access }: { clientId: string; access: Record<LetterArea, boolean> }) {
  const [params, setParams] = useSearchParams();
  const res = useApi<BriefeTabResponse>(`/api/kanzlei/briefe/${clientId}`, { live: ['briefe'], clientId });
  const [showDone, setShowDone] = useState(false);
  const open = params.get('brief');
  const d = res.data;

  const show = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('brief', id);
    else next.delete('brief');
    setParams(next, { replace: true });
  };

  if (!d) return res.error ? <p className="note">{res.error}</p> : <div className="spinner" aria-label="Lädt" />;
  const offen = d.letters.filter((l) => l.status === 'offen');
  const done = d.letters.filter((l) => l.status === 'erledigt');
  const both = access.fibu && access.lohn;

  return (
    <div className="briefe">
      <div className="card">
        <div className="ch">
          <h3>Briefe vom Mandanten</h3>
          <span>{offen.length} offen</span>
        </div>
        {offen.length ? (
          offen.map((l) => <LetterRow key={l.id} l={l} area={both} onOpen={show} />)
        ) : (
          <p className="note">
            Keine offenen Briefe. {d.client.contactName} lädt Post von Finanzamt, Krankenkasse &amp; Co. in der App hoch, sie
            erscheint dann hier.
          </p>
        )}
        {done.length > 0 && (
          <button
            type="button"
            className="btn sm"
            style={{ marginTop: 10 }}
            aria-expanded={showDone}
            onClick={() => setShowDone((x) => !x)}
          >
            {showDone ? 'Erledigte ausblenden ▴' : `Erledigte Briefe (${done.length}) ▾`}
          </button>
        )}
        {showDone && done.map((l) => <LetterRow key={l.id} l={l} area={both} onOpen={show} />)}
      </div>
      {open && <LetterDialog key={open} id={open} access={access} onClose={() => show(null)} />}
    </div>
  );
}

function LetterRow({ l, area, onOpen }: { l: KanzleiLetterItem; area: boolean; onOpen(id: string): void }) {
  const pill = l.status === 'offen' && l.due ? duePill(l.due) : null;
  const todo = l.status === 'offen' && (l.unseen || l.newComment);
  return (
    <div className="task">
      <span className="av" aria-hidden="true">
        ✉
      </span>
      <div className="tx">
        <b>{l.sender}</b>
        {/* wer beide Bereiche hat (oder der Admin), sieht, wohin der Brief gehört */}
        {area && <span className="note"> · {AREA_LABEL[l.area]}</span>}
        {l.unseen && (
          <>
            {' '}
            <span className="pill p-info">neu</span>
          </>
        )}
        {l.newComment && (
          <>
            {' '}
            <span className="pill p-warn">💬 neuer Kommentar</span>
          </>
        )}
        {pill && (
          <>
            {' '}
            <span className={`pill p-${pill.tone}`}>{pill.label}</span>
          </>
        )}
        {l.status === 'erledigt' && (
          <>
            {' '}
            <span className="pill p-ok">erledigt</span>
          </>
        )}
        {l.kiBetreff && <small className="ki-b">✨ {l.kiBetreff}</small>}
        <small>
          📄 {l.name} · {l.fromOffice ? 'eingescannt' : 'hochgeladen'} am {fmtDate(l.createdAt)}
          {l.createdBy ? ` von ${l.createdBy}` : ''}
          {l.status === 'erledigt' &&
            ` · erledigt${l.doneAt ? ` am ${fmtDate(l.doneAt)}` : ''}${l.doneBy ? ` von ${l.doneBy}` : ''}`}
        </small>
      </div>
      <button type="button" className={`btn sm${todo ? ' dark' : ''}`} onClick={() => onOpen(l.id)}>
        Ansehen
      </button>
    </div>
  );
}
